import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';

import { layout, media } from 'src/components/base';
import AddAlert from 'src/components/action-buttons/alert';
import { AddToListButton } from 'src/components/action-buttons/add-to-list';
import { ProductListCard } from 'src/components/product-card-new';
import { TooltipAlign } from 'src/components/tooltip-new';
import { DealsContext } from '../deals-context';
import { Ads } from '../../ads';
import HighlightedSearchResult from '../highlighted-search-result';

import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';
import { ActiveCampaignByProductId } from 'src/hooks/active-campaign-by-product-id';

import { isRaw } from '../../../utils/products';

import { LayoutMode } from 'src/components/layout-buttons/types';
import { ProductComparisonProps } from '../types';
import { Product, Raw, Offer } from 'src/types';
import { AdsLayoutConfig } from '../../ads/types';

import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

type ProductCardWrapperProps = {
  onClick?: (product: {
    id: string | number;
    name?: string;
    type?: string;
  }) => void;
  order: number;
  product: Product | Raw | Offer;
  productsWithActiveCampaign?: ActiveCampaignByProductId;
} & ProductComparisonProps;

const ProductCardWrapper = (props: ProductCardWrapperProps) => {
  const {
    deselectProductById,
    isSelectingProducts,
    onClick,
    order,
    product,
    productsWithActiveCampaign,
    selectedProducts,
    selectProductById,
  } = props;

  const { getDealDiffPercentage } = useContext(DealsContext);

  let id!: number;

  if (!isRaw(product)) {
    id = product.id;
  }

  const dealsDiffPercentage = productsWithActiveCampaign
    ? productsWithActiveCampaign[id]?.diffPercentage
    : getDealDiffPercentage(id);

  const isAuthenticated = useIsAuthenticated();

  const onSelect = (productId: number) =>
    selectProductById && selectProductById(productId, 'productCard');

  const onDeselect = (productId: number) =>
    deselectProductById && deselectProductById(productId, 'productCard');

  const onClickHandler = useCallback(
    () =>
      onClick && onClick({ id, name: product.name, type: product.__typename }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  const getPrice = () => {
    switch (product.__typename) {
      case 'Product':
        return product.priceSummary.regular;
      case 'Offer':
        return product.offerPrice.regular;
      case 'Raw':
        return product.price;
      default:
        return undefined;
    }
  };

  const disableUnfeaturedClickout = isRaw(product) && !product.store?.featured;

  // Related to isClickoutExperiment
  const renderActionArea = (p: Product) => {
    return (
      <>
        {isAuthenticated && (
          <AddToListWrapper>
            <AddToListButton
              productId={p.id}
              productName={p.name}
              tooltipAlign={TooltipAlign.LEFT}
              dropdownGrow="right"
            />{' '}
          </AddToListWrapper>
        )}
        <AddAlertWrapper>
          <AddAlert product={p} layout="button" />
        </AddAlertWrapper>
      </>
    );
  };

  return !isRaw(product) ? (
    <ProductCard
      key={product.id}
      container="li"
      id={product.id}
      brand={product.brand}
      highlightedName={
        product.highlight ? (
          <HighlightedSearchResult>{product.highlight}</HighlightedSearchResult>
        ) : (
          undefined
        )
      }
      imageSrc={product.media.first}
      imageSize={100}
      shouldLazyLoadImage
      name={product.name}
      link={product.pathName}
      onClick={onClickHandler}
      properties={product.properties?.nodes}
      price={product.priceSummary.regular}
      priceInclShipping={product.priceSummary.includeShipping}
      rank={product.popularity?.inCategory}
      rating={
        product.aggregatedRating?.score || product.userReviewSummary?.rating
      }
      reviewsCount={
        product.aggregatedRating?.count || product.userReviewSummary?.count
      }
      selectable={isSelectingProducts}
      selected={Boolean(selectedProducts?.find(p => p.id === product.id))}
      diffPercentage={dealsDiffPercentage}
      priceCount={product.priceSummary.count}
      ActionArea={renderActionArea(product)}
      onSelect={() => onSelect(product.id)}
      onDeselect={() => onDeselect(product.id)}
      $order={order}
    />
  ) : (
    <OfferCard
      type="offer"
      key={`${product.name}-${order}`}
      name={product.name}
      onClick={onClickHandler}
      price={getPrice()}
      store={product.store}
      link={disableUnfeaturedClickout ? undefined : product.externalUri}
      $order={order}
      imageSrc={
        product.__typename === 'Offer' ? product.media?.first : undefined
      }
    />
  );
};

type Props = {
  feedbackPrompt?: React.ReactNode | null;
  feedbackPromptPosition?: number;
  onClick?: (productId: string | number, name?: string, type?: string) => void;
  productsWithActiveCampaign?: ActiveCampaignByProductId;
  products: (Product | Raw)[];
  adslayoutConfig?: AdsLayoutConfig;
  embedPanoramaAd?: boolean;
  hasFeedback?: boolean;
} & ProductComparisonProps;

export const ListLayout = (props: Props) => {
  const {
    adslayoutConfig,
    embedPanoramaAd,
    feedbackPrompt,
    feedbackPromptPosition = 5,
    hasFeedback,
    onClick,
    products,
    productsWithActiveCampaign,
    ...productComparisonProps
  } = props;

  return (
    <List>
      {products.map((product, index) => (
        <ProductCardWrapper
          key={
            !isRaw(product) ? product.id : `${product.name}-${index.toString()}`
          }
          onClick={(productData: {
            id: string | number;
            name?: string;
            type?: string;
          }) => {
            onClick?.(productData.id, productData.name, productData.type);
            if (!isRaw(product))
              trackInternalNavigationClick({
                scope: InternalNavigationScope.LIST_LAYOUT_PRODUCT_CARD,
                nextPage: product.pathName,
                index,
              });
          }}
          order={index + 1}
          product={product}
          productsWithActiveCampaign={productsWithActiveCampaign}
          {...productComparisonProps}
        />
      ))}

      <Ads
        offersLength={products.length}
        layout={LayoutMode.LIST}
        layoutConfig={adslayoutConfig}
      />

      {hasFeedback && (
        <li style={{ order: feedbackPromptPosition - 1 }}>{feedbackPrompt}</li>
      )}
    </List>
  );
};

const List = styled.ul`
  display: grid;
  grid-template-rows: repeat(auto-fill, 1fr);
  gap: ${layout.measurements.spacing100};
  margin-bottom: ${layout.measurements.spacing100};
  list-style: none;
`;

const ProductCard = styled(ProductListCard)<{ $order: number }>`
  order: ${p => p.$order};
`;

const OfferCard = styled(ProductListCard)<{ $order: number }>`
  order: ${p => p.$order};
`;

const AddAlertWrapper = styled.div`
  position: absolute;
  top: ${layout.measurements.spacing100};
  right: ${layout.measurements.spacing100};

  ${media.mediumDown`
    display:none;
  `};
`;

const AddToListWrapper = styled.div`
  position: absolute;
  top: ${layout.measurements.spacing100};
  left: ${layout.measurements.spacing100};
`;
